import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Download,
  Play,
  Pause,
  Wand2,
  GitCompareArrows,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Terminal,
  FileText,
  Activity,
  Music,
  X,
} from "lucide-react";

import Header from "@/components/Header";
import ScoreRing from "@/components/ScoreRing";
import MetricCard from "@/components/MetricCard";
import SpectralBar from "@/components/SpectralBar";
import Waveform from "@/components/Waveform";
import Spectrogram from "@/components/Spectrogram";
import FonicoChat from "@/components/FonicoChat";
import {
  api,
  getAnalysis,
  optimizeTrack,
  optimizePreview,
  previewUrlById,
  audioUrl,
  downloadUrl,
  pdfUrl,
  compareTrack,
  uploadReference,
  deleteReference,
  getLufsPresets,
  listOptimizations,
  canGenerateOptimization,
  audioOptimizationUrl,
} from "@/lib/api";
import { useElapsedSeconds, fmtElapsed } from "@/lib/useElapsed";
import {
  playBeepOptimize,
  playUploadDone,
  playDownloadStart,
  playClickLufs,
  playClickReference,
} from "@/lib/beep";
import { ANALYSIS } from "@/constants/testIds";

// Client-side i18n for LUFS preset labels. The backend bakes the label
// into the stored analysis document at optimize() time using its own
// default `lang="it"`, so ALL existing records · and even fresh ones on
// pods that don't yet plumb the request lang through · would show the
// Italian string regardless of the current UI language. We render from
// the `lufs_preset` KEY (stored alongside the label) so switching UI
// language always reflects on every mastered version + preview card.
const KNOWN_PRESETS = new Set([
  "genre",
  "streaming",
  "cd",
  "club",
  "light",
  "reference",
  "custom",
]);
function translatePresetLabel(presetKey, storedLabel, t) {
  if (presetKey && KNOWN_PRESETS.has(presetKey)) {
    return t(`analysis.lufs_preset_labels.${presetKey}`);
  }
  return storedLabel || presetKey || "";
}

const METRIC_KEYS = ["lufs", "dynamic_range", "stereo_width", "spectral_balance", "punch", "clarity", "danceability"];

const METRIC_DETAILS = {
  lufs: (m) => `${m.lufs_integrated} dB`,
  dynamic_range: (m) => `${m.dynamic_range_db} dB`,
  stereo_width: (m) => `${(m.stereo_width * 100).toFixed(0)}%`,
  spectral_balance: () => "L/M/H",
  punch: () => "transient",
  clarity: () => "centroid",
  danceability: (m, a) => `${a.bpm} BPM`,
};

export default function Analysis() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const metricLabel = (k) => t(`analysis.metric_${k}`);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState(0);
  const [comparing, setComparing] = useState(false);
  const [spectrograms, setSpectrograms] = useState({}); // { [key]: { data: [...] } }
  const [selectedSpecKey, setSelectedSpecKey] = useState("original");
  const [progressOrig, setProgressOrig] = useState(-1);
  const [progressOpt, setProgressOpt] = useState(-1);
  const [lufsPreset, setLufsPreset] = useState("genre");
  const [previewing, setPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewBuster, setPreviewBuster] = useState(0);
  const [lufsPresets, setLufsPresets] = useState([]);
  const [optimizations, setOptimizations] = useState([]);
  const [additionalModalOpen, setAdditionalModalOpen] = useState(false);
  const [additionalPreset, setAdditionalPreset] = useState("streaming");
  const [additionalUseReference, setAdditionalUseReference] = useState(false);
  const [selectedOverlayKey, setSelectedOverlayKey] = useState(null);
  const [additionalSubmitting, setAdditionalSubmitting] = useState(false);
  const [additionalPrecheck, setAdditionalPrecheck] = useState(null);
  // Reference track (professional target for optimization retargeting).
  const [uploadingRef, setUploadingRef] = useState(false);
  const [useReferenceTarget, setUseReferenceTarget] = useState(false);

  // Auto-uncheck "use reference" once the reference target has been used
  // (either as preview or as full mastering). Without this, the toggle
  // stays visually "on" · which disables every other LUFS preset via the
  // `useReferenceTarget` guard · and the user is stuck with no clickable
  // target on the whole selector. Unchecking it here restores access to
  // the remaining unused presets.
  useEffect(() => {
    if (!useReferenceTarget) return;
    const inPreviews = (previewInfo?.previews || []).some(
      (x) => x.lufs_preset === "reference",
    );
    const inOpts = (optimizations || []).some(
      (o) => o.lufs_preset === "reference",
    );
    if (inPreviews || inOpts) setUseReferenceTarget(false);
  }, [useReferenceTarget, previewInfo, optimizations]);
  const refInput = useRef(null);
  const origAudioRef = useRef(null);
  const optAudioRef = useRef(null);
  const compareInput = useRef(null);
  const jobTimer = useRef(null);
  const optimizationRef = useRef(null);
  const masteredVersionsRef = useRef(null);
  const previewHbRef = useRef(null);
  const optimizeElapsed = useElapsedSeconds(optimizing);
  const previewElapsed = useElapsedSeconds(previewing);

  const seekOrig = useCallback((p) => {
    const a = origAudioRef.current;
    if (!a || !a.duration || !isFinite(a.duration)) return;
    a.currentTime = p * a.duration;
    setProgressOrig(p);
  }, []);

  const seekOpt = useCallback((p) => {
    const a = optAudioRef.current;
    if (!a || !a.duration || !isFinite(a.duration)) return;
    a.currentTime = p * a.duration;
    setProgressOpt(p);
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    getAnalysis(id)
      .then(setDoc)
      .catch(() => toast.error(t("analysis.not_found")))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    refresh();
    return () => {
      if (jobTimer.current) clearInterval(jobTimer.current);
    };
  }, [refresh]);

  // Scroll the page back to the top the moment the analysis becomes 'done'.
  // The user might have scrolled while the Home page was polling, and after
  // client-side navigation to /analysis/:id the browser preserves that
  // position · which lands them mid/bottom of the results screen instead of
  // at the score & verdict header. requestAnimationFrame guarantees the
  // scroll runs after React has committed the fresh DOM.
  useEffect(() => {
    if (!doc || doc.status !== "done") return;
    const ro = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    return () => cancelAnimationFrame(ro);
  }, [doc?.status]);

  // Whenever the OPTIMIZATION job transitions from running to finished,
  // React to `optimizing` state transitions:
  //   • Rising edge (idle → in-flight): scroll to the "Auto Optimization"
  //     card so the user immediately sees the progress bar. Remember how
  //     many masters already existed so we can pick the right end-scroll
  //     target on the falling edge.
  //   • Falling edge (in-flight → done):
  //       - FIRST master (started with 0 optimizations): scroll to the
  //         "Auto Optimization" card (Applied Preset + Score Improvements
  //         + OPTIMIZED player). That's the natural "here's your first
  //         result" landing spot the user expects.
  //       - ADDITIONAL master (started with ≥1 optimizations): scroll to
  //         "Your mastered versions" so the user sees the new card added
  //         to the list of downloads.
  //     The DOM target might not be ready yet when `setOptimizing(false)`
  //     fires (React commits `refreshOptimizations()` async), so we set a
  //     pending flag and let a second effect resolve the scroll via rAF
  //     retries up to 10 frames.
  const wasOptimizing = useRef(false);
  const optCountAtStart = useRef(0);
  const pendingScrollTarget = useRef(null); // "optimization" | "master" | null
  useEffect(() => {
    if (optimizing && !wasOptimizing.current) {
      wasOptimizing.current = true;
      optCountAtStart.current = optimizations?.length || 0;
      const ro = requestAnimationFrame(() => {
        optimizationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      return () => cancelAnimationFrame(ro);
    }
    if (!optimizing && wasOptimizing.current) {
      wasOptimizing.current = false;
      pendingScrollTarget.current =
        optCountAtStart.current === 0 ? "optimization" : "master";
    }
    return undefined;
  }, [optimizing, optimizations?.length]);

  // Perform the deferred scroll as soon as the target DOM node exists.
  // Retries via rAF for up to 30 frames (~500ms) to survive React's async
  // commit after `refresh()` + `refreshOptimizations()` resolve, plus a
  // second scheduled scroll ~600ms later so a re-render mid-smooth-scroll
  // (which can hijack the scroll on some browsers) can't leave us short.
  useEffect(() => {
    if (!pendingScrollTarget.current) return;
    let attempts = 0;
    let raf = 0;
    let followUp = 0;
    const target = pendingScrollTarget.current;
    const getNode = () => {
      if (target !== "master") {
        return (
          optimizationRef.current ||
          document.querySelector('[data-testid=auto-optimization-section]')
        );
      }
      // Prefer the LAST version card so the freshly generated one lands in
      // view · the section header alone may not be enough when many masters
      // stack up. Falls back to the section wrapper if no cards exist yet.
      const cards = document.querySelectorAll(
        '[data-testid=mastered-versions-section] [data-testid^=version-card-]',
      );
      const last = cards[cards.length - 1];
      return (
        last ||
        masteredVersionsRef.current ||
        document.querySelector('[data-testid=mastered-versions-section]')
      );
    };
    const tryScroll = () => {
      const node = getNode();
      if (node) {
        // For the "master" target center the card so surrounding cards stay
        // visible with a bit of padding. For "optimization" scroll the
        // Auto Optimization card to the top so the summary is the first
        // thing the user sees.
        node.scrollIntoView({
          behavior: "smooth",
          block: target === "master" ? "center" : "start",
        });
        // Defensive re-scroll: if a parent-driven re-render fires mid
        // smooth-scroll, snap the target back into view once things settle.
        followUp = setTimeout(() => {
          const n = getNode();
          n?.scrollIntoView({
            behavior: "smooth",
            block: target === "master" ? "center" : "start",
          });
        }, 700);
        pendingScrollTarget.current = null;
        return;
      }
      if (attempts++ < 30) {
        raf = requestAnimationFrame(tryScroll);
      } else {
        pendingScrollTarget.current = null;
      }
    };
    raf = requestAnimationFrame(tryScroll);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(followUp);
    };
  }, [optimizing, optimizations?.length]);

  // Refetch LUFS presets each time the UI language changes so labels follow.
  useEffect(() => {
    getLufsPresets()
      .then((d) => setLufsPresets(d.presets || []))
      .catch(() => {});
  }, [i18n.language]);

  // Ensure the preview heartbeat interval never leaks after unmount.
  useEffect(() => {
    return () => {
      if (previewHbRef.current) {
        clearInterval(previewHbRef.current);
        previewHbRef.current = null;
      }
    };
  }, []);

  // Global "one player at a time" · listens to the `play` event in capture
  // phase on the whole document and pauses every OTHER <audio> element.
  // Additive: does not touch per-player logic (Original↔Optimized mutual
  // pause via refs, previews, mastered versions accordion · all still work).
  useEffect(() => {
    const handlePlay = (e) => {
      const target = e.target;
      if (!target || target.tagName !== "AUDIO") return;
      document.querySelectorAll("audio").forEach((a) => {
        if (a !== target && !a.paused) {
          try { a.pause(); } catch { /* noop */ }
        }
      });
    };
    document.addEventListener("play", handlePlay, true);
    return () => document.removeEventListener("play", handlePlay, true);
  }, []);

  // If landed here while analysis is still 'pending', keep polling until 'done'
  useEffect(() => {
    if (!doc || doc.status !== "pending" || jobTimer.current) return;
    jobTimer.current = setInterval(() => {
      getAnalysis(id)
        .then((d) => {
          if (d.status !== "pending") {
            clearInterval(jobTimer.current);
            jobTimer.current = null;
            setDoc(d);
          }
        })
        .catch(() => {});
    }, 1500);
    return () => {
      if (jobTimer.current) {
        clearInterval(jobTimer.current);
        jobTimer.current = null;
      }
    };
  }, [doc, id]);

  // Load spectrogram for a given source key on demand. Cached client-side.
  // Tracks which keys are IN-FLIGHT via `spectrograms[key] === "loading"`
  // sentinel so the UI can render a skeleton without re-firing the fetch.
  const loadSpectrogram = React.useCallback(
    (key) => {
      if (!key || spectrograms[key]) return;
      setSpectrograms((prev) => ({ ...prev, [key]: "loading" }));
      api
        .get(`/spectrogram/${id}?which=${encodeURIComponent(key)}`)
        .then((r) => setSpectrograms((prev) => ({ ...prev, [key]: r.data })))
        .catch(() =>
          setSpectrograms((prev) => {
            // Drop the loading sentinel on error so a retry can happen.
            const next = { ...prev };
            delete next[key];
            return next;
          }),
        );
    },
    [id, spectrograms],
  );

  // Auto-load original when analysis first appears + PREFETCH all previews
  // and optimizations in the background so switching between sources is
  // instant. The backend caches on disk so subsequent requests are ~10ms.
  useEffect(() => {
    if (!doc) return;
    loadSpectrogram("original");
    (doc.previews || []).forEach((p) => {
      if (p?.preview_id) loadSpectrogram(`preview:${p.preview_id}`);
    });
    (doc.optimizations || []).forEach((o) => {
      if (o?.opt_id) loadSpectrogram(`opt:${o.opt_id}`);
    });
    // Legacy: pre-refactor doc had a single `optimized_path`.
    if (doc.optimized_path && (doc.optimizations || []).length === 0) {
      loadSpectrogram("optimized");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  // Auto-load whichever key is currently selected (idempotent).
  useEffect(() => {
    if (!doc || !selectedSpecKey) return;
    loadSpectrogram(selectedSpecKey);
  }, [doc, selectedSpecKey, loadSpectrogram]);

  // Prefetch new versions right after they are generated (previews +
  // optimizations arrive later via refresh/refreshOptimizations).
  useEffect(() => {
    (optimizations || []).forEach((o) => {
      if (o?.opt_id) loadSpectrogram(`opt:${o.opt_id}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizations?.length]);
  useEffect(() => {
    ((previewInfo?.previews) || []).forEach((p) => {
      if (p?.preview_id) loadSpectrogram(`preview:${p.preview_id}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewInfo?.previews?.length]);

  const pollJob = (jobId) => {
    // Tolerate transient network / 502 blips (Cloudflare + K8s ingress can 502
    // for a few seconds during high load). Only give up after 5 consecutive
    // failures · a single failed poll is not worth aborting the whole flow.
    let failCount = 0;
    const MAX_POLL_FAILS = 5;
    jobTimer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        failCount = 0; // reset on success
        setOptimizeProgress(data.progress || 0);
        if (data.status === "done") {
          clearInterval(jobTimer.current);
          setOptimizing(false);
          setSpectrograms((prev) => {
            // Purge any cached "optimized" (legacy) so it re-fetches with
            // the freshest master. Individual opt:/preview: keys survive.
            const next = { ...prev };
            delete next.optimized;
            return next;
          });
          playBeepOptimize();
          toast.success(t("analysis.toast_optimize_ready"));
          // Refresh doc + optimizations concurrently. The backend commits
          // the new master into `optimizations[]` BEFORE flipping the job
          // status to "done", so the fetch is guaranteed to see it · but
          // we retry once after 400ms as a safety net against propagation
          // delays on multi-pod deployments where the analysis GET might
          // hit a replica that hasn't caught up yet.
          refresh();
          refreshOptimizations();
          setTimeout(() => refreshOptimizations(), 500);
        } else if (data.status === "failed") {
          clearInterval(jobTimer.current);
          setOptimizing(false);
          toast.error(t("analysis.toast_optimize_error", { reason: data.error || t("analysis.unknown") }));
        }
      } catch (err) {
        failCount += 1;
        console.warn(`Optimize poll failed (${failCount}/${MAX_POLL_FAILS})`, err?.message);
        if (failCount >= MAX_POLL_FAILS) {
          clearInterval(jobTimer.current);
          setOptimizing(false);
          toast.error(t("analysis.toast_poll_error"));
        }
      }
    }, 2000);
  };

  useEffect(() => {
    if (doc && doc.preview_count > 0 && !previewInfo) {
      setPreviewInfo({
        used: doc.preview_count,
        limit: 2,
        admin: false,
        summary: doc.preview_summary || {},
        previews: (doc.previews || []).map((p) => ({
          ...p,
          url: previewUrlById(doc.id, p.preview_id, Date.now()),
        })),
      });
      setPreviewBuster(Date.now());
    }
  }, [doc, previewInfo]);

  const refreshOptimizations = useCallback(async () => {
    try {
      const res = await listOptimizations(id);
      setOptimizations(res.optimizations || []);
    } catch (err) {
      // Non-blocking: silently ignore.
      console.warn("optimizations refresh failed", err?.message);
    }
  }, [id]);

  useEffect(() => {
    if (!doc?.id) return;
    refreshOptimizations();
  }, [doc?.id, refreshOptimizations]);

  const onOptimize = async () => {
    setOptimizing(true);
    setOptimizeProgress(5);
    try {
      const res = await optimizeTrack(id, lufsPreset, null, useReferenceTarget);
      pollJob(res.job_id);
    } catch (err) {
      setOptimizing(false);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_preset") {
        toast.info(t("analysis.toast_duplicate_preset"));
        refreshOptimizations();
        return;
      }
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || t("analysis.toast_optimize_error_generic")
      );
    }
  };

  const openAdditionalModal = async () => {
    // Preselect a preset the user hasn't used yet.
    const usedPresets = new Set(optimizations.map((o) => o.lufs_preset));
    const firstFree =
      lufsPresets.find((p) => !usedPresets.has(p.key)) || lufsPresets[0];
    const initial = firstFree?.key || "streaming";
    setAdditionalPreset(initial);
    // If the reference target hasn't been used yet AND the user has a
    // reference uploaded, prefer that as the initial selection · it's the
    // most "personal" target and the one users tend to reach for.
    const referenceUsed = optimizations.some((o) => o.lufs_preset === "reference");
    const preselectRef = Boolean(doc?.reference) && !referenceUsed;
    setAdditionalUseReference(preselectRef);
    setAdditionalModalOpen(true);
    setAdditionalPrecheck(null);
    try {
      const key = preselectRef ? "reference" : initial;
      const pre = await canGenerateOptimization(id, key);
      setAdditionalPrecheck(pre);
    } catch {
      setAdditionalPrecheck(null);
    }
  };

  const onAdditionalPresetChange = async (preset) => {
    setAdditionalPreset(preset);
    setAdditionalUseReference(false);
    setAdditionalPrecheck(null);
    try {
      const pre = await canGenerateOptimization(id, preset);
      setAdditionalPrecheck(pre);
    } catch {
      setAdditionalPrecheck(null);
    }
  };

  const onAdditionalToggleReference = async (checked) => {
    setAdditionalUseReference(checked);
    setAdditionalPrecheck(null);
    try {
      const key = checked ? "reference" : additionalPreset;
      const pre = await canGenerateOptimization(id, key);
      setAdditionalPrecheck(pre);
    } catch {
      setAdditionalPrecheck(null);
    }
  };

  const confirmAdditional = async () => {
    if (additionalSubmitting) return;
    setAdditionalSubmitting(true);
    try {
      const res = await optimizeTrack(
        id,
        additionalPreset,
        null,
        additionalUseReference,
      );
      setAdditionalModalOpen(false);
      setOptimizing(true);
      setOptimizeProgress(5);
      pollJob(res.job_id);
      // Info toast about the START of the job. The actual "ready" toast is
      // emitted by pollJob() when the job completes · firing it here would
      // mis-inform the user that the file is already available.
      toast.info(t("analysis.toast_additional_processing"));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_preset") {
        toast.info(t("analysis.toast_duplicate_preset"));
        refreshOptimizations();
        setAdditionalModalOpen(false);
      } else if (status === 402) {
        toast.error(t("analysis.toast_no_credits"));
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message || t("analysis.toast_optimize_error_generic")
        );
      }
    } finally {
      setAdditionalSubmitting(false);
    }
  };

  // Two-phase fake progress heartbeat for the 30s preview HTTP call.
  // Mirrors the backend _optimize_progress_heartbeat pattern:
  //   Phase 1: fast climb 0 → 60 (+5pp every 700ms)  ≈ 8.4s
  //   Phase 2: slow crawl 60 → 92 (+1pp every 1200ms) ≈ 38s
  // Never reaches 100 · the real jump happens when the HTTP call resolves.
  const startPreviewHeartbeat = useCallback(() => {
    if (previewHbRef.current) clearInterval(previewHbRef.current);
    setPreviewProgress(0);
    let pct = 0;
    const tick = () => {
      const step = pct < 60 ? 5 : 1;
      const interval = pct < 60 ? 700 : 1200;
      pct = Math.min(pct + step, 92);
      setPreviewProgress(pct);
      if (pct >= 92) {
        clearInterval(previewHbRef.current);
        previewHbRef.current = null;
        return;
      }
      // Reset interval so the tick rhythm slows once we enter phase 2.
      clearInterval(previewHbRef.current);
      previewHbRef.current = setInterval(tick, interval);
    };
    previewHbRef.current = setInterval(tick, 700);
  }, []);

  const stopPreviewHeartbeat = useCallback((finalPct = 100) => {
    if (previewHbRef.current) {
      clearInterval(previewHbRef.current);
      previewHbRef.current = null;
    }
    setPreviewProgress(finalPct);
    // Fade out the bar shortly after completion.
    setTimeout(() => setPreviewProgress(0), 600);
  }, []);

  const onPreview = async () => {
    setPreviewing(true);
    startPreviewHeartbeat();
    try {
      const res = await optimizePreview(id, lufsPreset, null, useReferenceTarget);
      setPreviewInfo({
        used: res.preview_count,
        limit: res.preview_limit,
        admin: res.admin,
        summary: res.summary,
        previews: res.previews || [],
      });
      setPreviewBuster(Date.now());
      stopPreviewHeartbeat(100);
      // Notify the user that the preview is ready even if their tab is in
      // the background (a preview takes 8-15s and users often switch away).
      playBeepOptimize();
      toast.success(t("analysis.preview_ready_title"));
    } catch (err) {
      stopPreviewHeartbeat(0);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Same-preset dedup on preview → soft info, refresh state.
      if (status === 409 && detail?.code === "duplicate_preset") {
        toast.info(t("analysis.toast_duplicate_preset"));
        return;
      }
      // NEVER pass an object to toast.error · sonner renders the arg as a
      // React child which crashes the whole tree ("black screen") when the
      // backend returns `detail = {code, message, ...}` on 4xx errors.
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || t("analysis.toast_optimize_error_generic")
      );
    } finally {
      setPreviewing(false);
    }
  };

  // Step 1: user picks the comparison file → stash it and open the copyright
  // consent modal. We DO NOT upload yet · the user must explicitly reconfirm
  // ownership rights over this second audio file before it hits the server.
  const onCompareFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    doCompareUpload(f);
  };

  // Copyright consent no longer required for A/B compare (see task 2026-09):
  // the user already accepted at analysis time.
  const doCompareUpload = async (f) => {
    if (!f) return;
    setComparing(true);
    try {
      await compareTrack(id, f);
      toast.success(t("analysis.toast_compare_done"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("analysis.toast_compare_error"));
    } finally {
      setComparing(false);
    }
  };

  const onReferenceFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingRef(true);
    try {
      const ref = await uploadReference(id, f);
      playUploadDone();
      toast.success(t("analysis.ref_uploaded"));
      // Merge reference into local doc state so the reference chip + toggle
      // appear WITHOUT triggering a full page refresh (which was scrolling
      // the user to the top). Toggle stays OFF · user must opt-in explicitly.
      setDoc((prev) => (prev ? { ...prev, reference: ref } : prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("analysis.ref_error"));
    } finally {
      setUploadingRef(false);
    }
  };

  const onReferenceDelete = async () => {
    try {
      await deleteReference(id);
      setUseReferenceTarget(false);
      setDoc((prev) => (prev ? { ...prev, reference: undefined } : prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("analysis.ref_error"));
    }
  };

  if (loading || !doc) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="animate-spin text-[color:var(--brand)]" size={32} />
        </div>
      </div>
    );
  }

  if (doc.status === "pending" || doc.status === "processing" || !doc.analysis) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-[900px] mx-auto px-6 py-16 relative z-10">
          <div className="card-elev p-10 flex flex-col items-center text-center gap-4">
            <Loader2 className="animate-spin text-[color:var(--brand)]" size={40} />
            <h2 className="font-display text-2xl text-white">{t("analysis.processing_title")}</h2>
            <p className="text-[color:var(--fg-secondary)] max-w-md">
              {t("analysis.processing_body_prefix")}{" "}
              <span className="text-white">{doc.filename}</span>{" "}
              {t("analysis.processing_body_suffix", { genre: doc.genre })}
            </p>
            <div className="tiny-caps mt-2">
              {doc.status === "processing"
                ? t("analysis.processing_stage_processing")
                : t("analysis.processing_stage_pending")}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (doc.status === "failed") {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-[900px] mx-auto px-6 py-16 relative z-10">
          <div className="card-elev p-10 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center text-[color:var(--meter-poor)] text-2xl">!</div>
            <h2 className="font-display text-2xl text-white">{t("analysis.failed_title")}</h2>
            <p className="text-[color:var(--fg-secondary)] max-w-md">
              {doc.error || t("analysis.failed_body_default")}
            </p>
            <button onClick={() => nav("/")} className="btn-primary" data-testid={ANALYSIS.backHome}>
              {t("analysis.back_to_upload")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { analysis, feedback, waveform, optimized_analysis, optimize_summary, optimized_waveform, comparisons, chat_history } = doc;
  const scores = analysis.scores;
  const meas = analysis.measurements;
  const optimizedScores = optimized_analysis?.scores;

  return (
    <div className="min-h-screen" data-testid={ANALYSIS.dashboard}>
      <Header
        right={
          <>
            <a
              href={pdfUrl(id)}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
              data-testid="pdf-download"
              onClick={() => playDownloadStart()}
            >
              <FileText size={14} /> PDF
            </a>
            <button
              className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
              onClick={() => nav("/")}
              data-testid={ANALYSIS.backHome}
            >
              <ArrowLeft size={14} /> {t("header.new")}
            </button>
          </>
        }
      />

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 relative z-10">
        {doc.lang && doc.lang !== i18n.language && (
          <div
            className="card-elev border-l-4 border-l-[color:var(--brand)] px-5 py-3 flex items-start gap-3 mb-6 text-sm fade-up"
            data-testid="lang-mismatch-notice"
          >
            <span className="tiny-caps text-[color:var(--brand)]">i18n</span>
            <div className="text-[color:var(--fg-secondary)]">
              {t("analysis.lang_mismatch_notice", { lang: doc.lang.toUpperCase() })}
            </div>
          </div>
        )}
        {/* Meta */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 fade-up">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="tiny-caps text-[color:var(--brand)]">{t("analysis.analysis_id")} #{id.slice(0, 8)}</span>
              <span className="tiny-caps opacity-40">·</span>
              <span className="tiny-caps">{doc.mode === "mix" ? t("analysis.mode_mix_label") : t("analysis.mode_mastering_label")}</span>
              <span className="tiny-caps opacity-40">·</span>
              <span className="tiny-caps">{doc.genre}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white truncate max-w-[700px]">
              {doc.filename}
            </h1>
            <div className="text-sm text-[color:var(--fg-secondary)] metric-value mt-2">
              {analysis.duration_s.toFixed(1)}s · {analysis.bpm} BPM · {meas.lufs_integrated} LUFS · {meas.true_peak_db} dBTP
            </div>
          </div>
        </div>

        {/* Waveform hero */}
        <section className="card p-5 mb-8 fade-up" style={{ animationDelay: "40ms" }}>
          {(() => {
            // Aggregate every version the user can compare visually against
            // the ORIGINAL: previews (short 30s clips) + optimizations
            // (full masters). Newest last so the auto-selection points at
            // the freshest thing the user just generated.
            const opts = optimizations || [];
            const prevs = (previewInfo?.previews) || [];
            const overlaySources = [
              ...prevs.map((p) => ({
                key: `preview:${p.preview_id}`,
                kind: "preview",
                preset: p.lufs_preset,
                label: translatePresetLabel(
                  p.lufs_preset,
                  (p.summary || {}).lufs_preset_label,
                  t,
                ),
                waveform: p.preview_waveform || [],
              })),
              ...opts.map((o) => ({
                key: `opt:${o.opt_id}`,
                kind: "opt",
                preset: o.lufs_preset,
                label: translatePresetLabel(
                  o.lufs_preset,
                  (o.summary || {}).lufs_preset_label,
                  t,
                ),
                waveform: o.optimized_waveform || [],
              })),
            ].filter((s) => Array.isArray(s.waveform) && s.waveform.length > 0);
            // Legacy fallback for records generated before we started saving
            // per-version waveforms (only fires when no per-version data).
            if (overlaySources.length === 0 && Array.isArray(optimized_waveform) && optimized_waveform.length > 0) {
              overlaySources.push({
                key: "legacy",
                kind: "opt",
                preset: (optimize_summary || {}).lufs_preset,
                label: translatePresetLabel(
                  (optimize_summary || {}).lufs_preset,
                  (optimize_summary || {}).lufs_preset_label,
                  t,
                ),
                waveform: optimized_waveform,
              });
            }
            // Pick auto-selection: last item = most recent overall.
            const autoKey = overlaySources.length
              ? overlaySources[overlaySources.length - 1].key
              : null;
            const activeKey = overlaySources.some((s) => s.key === selectedOverlayKey)
              ? selectedOverlayKey
              : autoKey;
            const active = overlaySources.find((s) => s.key === activeKey);
            const hasOverlay = Boolean(active);
            return (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="tiny-caps">{t("analysis.waveform")}</div>
                  {hasOverlay && (
                    <div className="text-xs text-[color:var(--fg-secondary)] flex flex-wrap items-center gap-3">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-sm bg-[color:var(--brand)]" /> {t("analysis.original")}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-sm bg-[color:var(--meter-excellent)]" /> {t("analysis.optimized")}
                      </span>
                      {overlaySources.length > 1 ? (
                        <div className="flex items-center gap-1 flex-wrap" data-testid="waveform-overlay-switcher">
                          {overlaySources.map((s) => (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => setSelectedOverlayKey(s.key)}
                              className={`pill !py-1 !px-2 text-[10px] ${activeKey === s.key ? "active" : ""}`}
                              data-testid={`waveform-overlay-${s.kind}-${s.preset}`}
                              title={s.label}
                            >
                              <span className="opacity-70 mr-1">
                                {s.kind === "preview"
                                  ? t("analysis.preview_label", { defaultValue: "Preview" })
                                  : t("analysis.master_label", { defaultValue: "Master" })}
                              </span>
                              <span className="font-medium">{s.label.split(" (")[0]}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[color:var(--fg-muted)] normal-case">
                          · {active?.label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Waveform
                  data={waveform || []}
                  secondary={hasOverlay ? active.waveform : undefined}
                  secondaryColor="var(--meter-excellent)"
                  progress={progressOrig}
                  onSeek={seekOrig}
                />
              </>
            );
          })()}
        </section>

        {/* Top: Hit + Dance + Overall verdict */}
        <section className="grid lg:grid-cols-[280px_280px_1fr] gap-6 mb-10 fade-up" style={{ animationDelay: "60ms" }}>
          <div className="card p-6 flex flex-col items-center gap-3" data-testid={ANALYSIS.hitCard}>
            <div className="tiny-caps">{t("analysis.hit_potential")}</div>
            <ScoreRing value={scores.hit_potential} color="var(--meter-hit)" size={160} />
            <div className="text-xs text-center text-[color:var(--fg-secondary)]">
              {t("analysis.hit_vs_top")} · {doc.genre}
            </div>
          </div>
          <div className="card p-6 flex flex-col items-center gap-3" data-testid={ANALYSIS.danceCard}>
            <div className="tiny-caps">{t("analysis.danceability")}</div>
            <ScoreRing value={scores.danceability} color="var(--meter-dance)" size={160} />
            <div className="text-xs text-center text-[color:var(--fg-secondary)]">
              {t("analysis.danceability_desc", { bpm: analysis.bpm, min: analysis.targets.bpm_range[0], max: analysis.targets.bpm_range[1] })}
            </div>
          </div>
          <div className="card p-6 flex flex-col gap-3" data-testid={ANALYSIS.overallFeedback}>
            <div className="flex items-center gap-2">
              <Terminal size={16} className="text-[color:var(--brand)]" />
              <div className="tiny-caps">{t("analysis.engineer_verdict")}</div>
            </div>
            <p className="text-[15px] leading-relaxed text-white">{feedback?.overall_verdict}</p>
            {feedback?.strengths?.length > 0 && (
              <div className="mt-2 pt-3 border-t border-white/5">
                <div className="tiny-caps mb-2">{t("analysis.strengths")}</div>
                <ul className="space-y-1.5">
                  {feedback.strengths.map((s, i) => (
                    <li key={`strength-${i}-${(s || "").slice(0, 20)}`} className="text-sm text-[color:var(--fg-secondary)] flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[color:var(--meter-excellent)] shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {feedback?.hit_verdict && (
              <div className="mt-2 pt-3 border-t border-white/5">
                <div className="tiny-caps mb-1">{t("analysis.hit_analysis")}</div>
                <p className="text-sm text-[color:var(--fg-secondary)] leading-relaxed">
                  {feedback.hit_verdict}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Metric grid */}
        <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 fade-up" style={{ animationDelay: "120ms" }}>
          {METRIC_KEYS.map((key) => (
            <MetricCard
              key={key}
              title={metricLabel(key)}
              value={scores[key]}
              feedback={feedback?.improvements?.[key]}
              detail={METRIC_DETAILS[key]?.(meas, analysis)}
              tone={key === "danceability" ? "dance" : undefined}
            />
          ))}
        </section>

        {/* Spectral balance + Spectrogram */}
        <section className="grid lg:grid-cols-[1fr_1.2fr] gap-6 mb-10 fade-up" style={{ animationDelay: "150ms" }}>
          <SpectralBar current={meas.spectral} target={analysis.targets.spectral} />
          <div className="card p-5 flex flex-col gap-3">
            {(() => {
              // Same aggregation as the waveform hero: original + every
              // preview + every full mastering. Auto-loads the selected
              // spectrogram from the backend on demand and caches it.
              const opts = optimizations || [];
              const prevs = (previewInfo?.previews) || [];
              const sources = [
                { key: "original", kind: "original", label: t("analysis.original") },
                ...prevs.map((p) => ({
                  key: `preview:${p.preview_id}`,
                  kind: "preview",
                  preset: p.lufs_preset,
                  label: translatePresetLabel(
                    p.lufs_preset,
                    (p.summary || {}).lufs_preset_label,
                    t,
                  ),
                })),
                ...opts.map((o) => ({
                  key: `opt:${o.opt_id}`,
                  kind: "opt",
                  preset: o.lufs_preset,
                  label: translatePresetLabel(
                    o.lufs_preset,
                    (o.summary || {}).lufs_preset_label,
                    t,
                  ),
                })),
              ];
              // Legacy fallback: if there are no per-version optimizations
              // yet, but the doc still has the pre-refactor `optimized_path`,
              // expose it as a synthetic "optimized" source so old records
              // keep the Original/Optimized toggle.
              if (opts.length === 0 && optimize_summary && doc?.optimized_path) {
                sources.push({
                  key: "optimized",
                  kind: "opt",
                  preset: optimize_summary.lufs_preset,
                  label: translatePresetLabel(
                    optimize_summary.lufs_preset,
                    optimize_summary.lufs_preset_label,
                    t,
                  ),
                });
              }
              const activeKey = sources.some((s) => s.key === selectedSpecKey)
                ? selectedSpecKey
                : "original";
              const activeSpec = spectrograms[activeKey];
              return (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Activity size={16} className="text-[color:var(--brand)]" />
                    <div className="tiny-caps">{t("analysis.mel_spectrogram")}</div>
                    {sources.length > 1 && (
                      <div
                        className="ml-auto flex items-center gap-1 flex-wrap"
                        data-testid="spectrogram-switcher"
                      >
                        {sources.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => setSelectedSpecKey(s.key)}
                            className={`pill !py-1 !px-2 text-[10px] ${activeKey === s.key ? "active" : ""}`}
                            data-testid={`spectrogram-src-${s.kind}${s.preset ? `-${s.preset}` : ""}`}
                            title={s.label}
                          >
                            {s.kind !== "original" && (
                              <span className="opacity-70 mr-1">
                                {s.kind === "preview"
                                  ? t("analysis.preview_label", { defaultValue: "Preview" })
                                  : t("analysis.master_label", { defaultValue: "Master" })}
                              </span>
                            )}
                            <span className="font-medium">{s.label.split(" (")[0]}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Spectrogram
                    data={typeof activeSpec === "string" ? [] : (activeSpec?.data || [])}
                    loading={
                      activeSpec === "loading" ||
                      (!activeSpec && Boolean(sources.find((s) => s.key === activeKey)))
                    }
                    progress={activeKey === "original" ? progressOrig : progressOpt}
                    onSeek={activeKey === "original" ? seekOrig : seekOpt}
                  />
                  <div className="text-xs text-[color:var(--fg-muted)] metric-value">
                    {t("analysis.mel_spectrogram_desc")}
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        {/* Optimization + Player */}
        <section
          ref={optimizationRef}
          className="grid lg:grid-cols-2 gap-6 mb-10 fade-up"
          style={{ animationDelay: "180ms" }}
          data-testid="auto-optimization-section"
        >
          <div className="card-elev p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Wand2 size={18} className="text-[color:var(--brand)]" />
              <h3 className="font-display text-xl font-semibold">{t("analysis.auto_optimization")}</h3>
            </div>
            <p className="text-sm text-[color:var(--fg-secondary)]">
              {t("analysis.auto_optimization_desc")}
            </p>
            {optimizing ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="animate-spin" size={16} />
                  <span>{t("analysis.processing_pct", { pct: optimizeProgress })}</span>
                  {optimizeElapsed > 0 && (
                    <span
                      className="text-[10px] opacity-60 metric-value ml-auto"
                      data-testid="optimize-elapsed"
                    >
                      {fmtElapsed(optimizeElapsed)}
                    </span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[color:var(--brand)] transition-all"
                    style={{ width: `${optimizeProgress}%` }}
                  />
                </div>
              </div>
            ) : !optimize_summary ? (
              <div className="flex flex-col gap-3">
                {/* Reference upload + "Optimize toward reference" toggle */}
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex flex-col gap-2" data-testid="reference-section">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <Music size={14} className="text-[color:var(--brand)]" />
                      <span className="tiny-caps">{t("analysis.reference_title", { defaultValue: "Reference" })}</span>
                    </div>
                    <input
                      ref={refInput}
                      type="file"
                      accept=".mp3,.aiff,.aif,.flac,.wav"
                      className="hidden"
                      onChange={onReferenceFile}
                      data-testid="reference-input"
                    />
                    {!doc.reference ? (
                      <button
                        className="btn-ghost flex items-center gap-2 text-xs"
                        onClick={() => refInput.current?.click()}
                        disabled={uploadingRef}
                        data-testid="reference-upload-btn"
                      >
                        {uploadingRef ? <Loader2 className="animate-spin" size={14} /> : <Music size={14} />}
                        {t("analysis.reference_upload", { defaultValue: "Carica reference" })}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[11px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 truncate max-w-[220px]"
                          title={doc.reference.filename}
                          data-testid="reference-chip"
                        >
                          {doc.reference.filename}
                        </span>
                        <button
                          onClick={onReferenceDelete}
                          className="p-1 rounded hover:bg-red-500/15 text-[color:var(--fg-muted)] hover:text-red-300"
                          title={t("analysis.reference_remove", { defaultValue: "Rimuovi reference" })}
                          data-testid="reference-remove-btn"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                  {doc.reference && (() => {
                    // Same dedup rule as the LUFS preset pills: if a preview
                    // or a full optimization with preset="reference" already
                    // exists on this analysis, we grey out the toggle so the
                    // user cannot select the same target twice.
                    const refUsedInPreviews = (previewInfo?.previews || []).some(
                      (x) => x.lufs_preset === "reference",
                    );
                    const refUsedInOpts = (optimizations || []).some(
                      (o) => o.lufs_preset === "reference",
                    );
                    const refAlreadyUsed = refUsedInPreviews || refUsedInOpts;
                    return (
                    <label
                      className={`flex items-center gap-2 text-xs text-[color:var(--fg-secondary)] select-none ${refAlreadyUsed ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid="use-reference-toggle-label"
                      title={refAlreadyUsed ? t("analysis.preset_already_used", { defaultValue: "Preset già usato per una preview o un master di questa traccia" }) : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={useReferenceTarget}
                        disabled={refAlreadyUsed}
                        onChange={(e) => {
                          setUseReferenceTarget(e.target.checked);
                          playClickReference(e.target.checked);
                        }}
                        className="accent-[color:var(--brand)]"
                        data-testid="use-reference-toggle"
                      />
                      <span>{t("analysis.reference_toggle", { defaultValue: "Ottimizza verso reference (usa i suoi parametri come target invece dei preset)" })}</span>
                      {refAlreadyUsed && (
                        <span className="text-[9px] tiny-caps text-emerald-300/80">✓</span>
                      )}
                    </label>
                    );
                  })()}
                </div>

                <div>
                  <div className="tiny-caps mb-2">{t("analysis.lufs_target_label")}</div>
                  <div className="flex flex-wrap gap-2" data-testid="lufs-preset-selector">
                    {lufsPresets.map((p) => {
                      // Preset is considered "used" if a preview OR a full
                      // optimization with the same key already exists on
                      // this analysis. We disable the button so the user
                      // can't retrigger the same target (backend also
                      // returns 409, but a disabled UI is clearer and
                      // avoids a needless round-trip).
                      const usedInPreviews = (previewInfo?.previews || []).some(
                        (x) => x.lufs_preset === p.key,
                      );
                      const usedInOpts = (optimizations || []).some(
                        (o) => o.lufs_preset === p.key,
                      );
                      const alreadyUsed = usedInPreviews || usedInOpts;
                      // NB: `useReferenceTarget` no longer disables the
                      // other pills · clicking a LUFS pill simply unchecks
                      // the reference toggle so the user can freely switch
                      // between "reference-driven" and preset-driven
                      // targets without having to first uncheck a box.
                      const isActive = !useReferenceTarget && lufsPreset === p.key;
                      return (
                        <button
                          key={p.key}
                          className={`pill ${isActive ? "active" : ""}`}
                          onClick={() => {
                            setLufsPreset(p.key);
                            if (useReferenceTarget) setUseReferenceTarget(false);
                            playClickLufs();
                          }}
                          disabled={alreadyUsed}
                          data-testid={`lufs-preset-${p.key}`}
                          title={
                            alreadyUsed
                              ? t("analysis.preset_already_used", { defaultValue: "Preset già usato per una preview o un master di questa traccia" })
                              : (p.lufs != null ? `${p.lufs} LUFS` : t("analysis.preset_title"))
                          }
                        >
                          <span className="font-medium">{p.label.split(" (")[0]}</span>
                          {p.lufs != null && (
                            <span className="metric-value text-[10px] opacity-70 ml-1.5">
                              {p.lufs} LUFS
                            </span>
                          )}
                          {alreadyUsed && (
                            <span className="ml-1.5 text-[9px] tiny-caps text-emerald-300/80">✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs text-[color:var(--fg-muted)] mt-2">
                    {useReferenceTarget ? (
                      <span className="text-emerald-300">
                        {t("analysis.reference_target_desc", { defaultValue: "Target derivato dalla reference: LUFS, bilanciamento frequenze, dinamica e stereo verranno applicati direttamente dai suoi parametri." })}
                      </span>
                    ) : (
                      <>
                        {lufsPreset === "streaming" && t("analysis.preset_streaming_desc")}
                        {lufsPreset === "cd" && t("analysis.preset_cd_desc")}
                        {lufsPreset === "club" && t("analysis.preset_club_desc")}
                        {lufsPreset === "light" && t("analysis.preset_light_desc")}
                        {lufsPreset === "genre" && t("analysis.preset_genre_desc", { genre: doc.genre, mode: doc.mode })}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 self-start">
                  <div className="flex flex-col gap-1 min-w-[180px]">
                    <button
                      className="btn-ghost flex items-center justify-center gap-2"
                      onClick={onPreview}
                      disabled={previewing || (previewInfo && !previewInfo.admin && previewInfo.used >= previewInfo.limit)}
                      data-testid="preview-btn"
                      title={
                        previewInfo && !previewInfo.admin && previewInfo.used >= previewInfo.limit
                          ? t("analysis.preview_limit_reached")
                          : ""
                      }
                    >
                      {previewing ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
                      {t("analysis.preview_btn")}
                      {previewInfo && !previewInfo.admin && (
                        <span className="metric-value text-[10px] opacity-70 ml-1">
                          {previewInfo.used}/{previewInfo.limit}
                        </span>
                      )}
                    </button>
                    {(previewing || previewProgress > 0) && (
                      <div className="flex items-center gap-2">
                        <div
                          className="h-1 flex-1 rounded-full bg-white/5 overflow-hidden"
                          data-testid="preview-progress"
                        >
                          <div
                            className="h-full bg-[color:var(--brand)] transition-all duration-500 ease-out"
                            style={{ width: `${previewProgress}%` }}
                          />
                        </div>
                        {previewing && previewElapsed > 0 && (
                          <span
                            className="text-[10px] opacity-60 metric-value tabular-nums"
                            data-testid="preview-elapsed"
                          >
                            {fmtElapsed(previewElapsed)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn-primary flex items-center justify-center gap-2"
                    onClick={onOptimize}
                    data-testid={ANALYSIS.optimizeBtn}
                  >
                    <Wand2 size={18} /> {t("analysis.optimize_btn")}
                  </button>
                </div>
                {previewInfo && (previewInfo.previews?.length ?? 0) > 0 && (
                  <div className="flex flex-col gap-3" data-testid="preview-player">
                    {/* Show ALL previews (up to 2 useful ones) so the user
                        can A/B compare and pick their preferred LUFS target
                        BEFORE committing to the final optimize. */}
                    {previewInfo.previews.map((p, idx) => {
                      const isSelected = lufsPreset === p.lufs_preset;
                      return (
                        <div
                          key={p.preview_id || idx}
                          role="button"
                          tabIndex={0}
                          onClick={() => setLufsPreset(p.lufs_preset)}
                          onKeyDown={(e) => {
                            // Space/Enter also selects · but let the audio
                            // controls handle their own keyboard interactions.
                            if ((e.key === "Enter") && e.target === e.currentTarget) {
                              setLufsPreset(p.lufs_preset);
                            }
                          }}
                          className={`rounded-lg border p-3 flex flex-col gap-2 transition-colors cursor-pointer ${
                            isSelected
                              ? "border-[color:var(--brand)] bg-blue-500/10"
                              : "border-[color:var(--brand)]/30 bg-blue-500/5 hover:border-[color:var(--brand)]/60"
                          }`}
                          data-testid={`preview-item-${p.lufs_preset}`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="tiny-caps">{t("analysis.preview_ready_title")}</span>
                              {isSelected && (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--brand)]/25 text-[color:var(--brand)] font-semibold">
                                  {t("analysis.preview_selected") || "Selezionato"}
                                </span>
                              )}
                            </div>
                            <div className="metric-value text-[10px] text-[color:var(--fg-muted)] tabular-nums">
                              {p.summary?.loudness_measured_lufs} LUFS
                            </div>
                          </div>
                          <audio
                            controls
                            src={previewUrlById(id, p.preview_id, previewBuster)}
                            className="w-full"
                            onError={(e) => {
                              if (e?.target?.error?.code === 4) {
                                toast.error(t("analysis.toast_audio_unavailable", { defaultValue: "Audio non disponibile" }));
                              }
                            }}
                            data-testid={`preview-audio-${p.lufs_preset}`}
                          />
                          {/* LUFS target label under each preview · exactly
                              what the user asked to see so they can compare. */}
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-[11px] text-[color:var(--fg-secondary)] leading-tight">
                              <span className="text-[color:var(--fg-muted)] tiny-caps mr-1">
                                {t("analysis.preview_lufs_target_label") || "LUFS target"}:
                              </span>
                              <span className="font-medium text-white">
                                {translatePresetLabel(p.lufs_preset, p.summary?.lufs_preset_label, t)}
                              </span>
                              <span className="text-[color:var(--fg-muted)] ml-1">
                                ({p.summary?.loudness_target_lufs ?? "?"} LUFS)
                              </span>
                            </p>
                            {!isSelected && (
                              <button
                                type="button"
                                onClick={() => setLufsPreset(p.lufs_preset)}
                                className="text-[11px] px-2.5 py-1 rounded-md bg-[color:var(--brand)]/15 hover:bg-[color:var(--brand)]/25 active:bg-[color:var(--brand)]/35 text-[color:var(--brand)] border border-[color:var(--brand)]/40 font-medium transition-colors"
                                data-testid={`preview-choose-${p.lufs_preset}`}
                              >
                                {t("analysis.preview_choose_this") || "Usa questo"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-[color:var(--fg-secondary)]">
                      {t("analysis.preview_ready_desc", {
                        used: previewInfo.used,
                        limit: previewInfo.limit,
                      })}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-[color:var(--meter-excellent)]/30 bg-emerald-500/5 p-3">
                  <div className="tiny-caps mb-1">{t("analysis.preset_applied")}</div>
                  <div className="text-sm text-white font-medium">
                    {translatePresetLabel(
                      optimize_summary.lufs_preset,
                      lufsPresets.find((p) => p.key === optimize_summary.lufs_preset)?.label ||
                        optimize_summary.lufs_preset_label,
                      t,
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--fg-secondary)] mt-1 metric-value">
                    {t("analysis.preset_summary", { target: optimize_summary.loudness_target_lufs })}{" "}
                    <span className="text-white">{optimize_summary.loudness_measured_lufs} LUFS</span>{" "}
                    {t("analysis.preset_delta", {
                      sign: optimize_summary.loudness_delta_db > 0 ? "+" : "",
                      delta: optimize_summary.loudness_delta_db,
                      iter: optimize_summary.iterations,
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <SmallStat label={t("analysis.eq_low")} value={`${optimize_summary.eq_low_db > 0 ? "+" : ""}${optimize_summary.eq_low_db} dB`} />
                  <SmallStat label={t("analysis.eq_high")} value={`${optimize_summary.eq_high_db > 0 ? "+" : ""}${optimize_summary.eq_high_db} dB`} />
                  <SmallStat label={t("analysis.stereo_delta")} value={optimize_summary.stereo_delta.toFixed(2)} />
                  <SmallStat label={t("analysis.ceiling")} value={`${optimize_summary.true_peak_ceiling_db} dBTP`} />
                  <SmallStat label={t("analysis.compression")} value={optimize_summary.compression_applied ? t("analysis.yes") : t("analysis.no")} />
                  <SmallStat label={t("analysis.iterations")} value={String(optimize_summary.iterations || 1)} />
                </div>
                <a
                  href={downloadUrl(id)}
                  className="btn-primary flex items-center justify-center gap-2 self-start no-underline"
                  data-testid={ANALYSIS.downloadBtn}
                  onClick={() => playDownloadStart()}
                >
                  <Download size={18} /> {t("analysis.download_wav")}
                </a>
              </div>
            )}
          </div>

          <div className="card-elev p-6 flex flex-col gap-4">
            <div className="tiny-caps">{t("analysis.audio_preview")}</div>
            <PlayerRow
              label={t("analysis.original")}
              src={audioUrl(id, "original")}
              testid={ANALYSIS.originalPlayer}
              audioRef={origAudioRef}
              onProgress={setProgressOrig}
              onPlayStart={() => {
                // Pause the sibling optimized player when Original starts.
                try { optAudioRef.current?.pause(); } catch { /* noop */ }
              }}
              playLabel={t("analysis.play")}
              pauseLabel={t("analysis.pause")}
            />
            {optimize_summary && (
              <PlayerRow
                label={t("analysis.optimized")}
                src={audioUrl(id, "optimized")}
                testid={ANALYSIS.optimizedPlayer}
                highlight
                audioRef={optAudioRef}
                onProgress={setProgressOpt}
                onPlayStart={() => {
                  // Pause the sibling original player when Optimized starts.
                  try { origAudioRef.current?.pause(); } catch { /* noop */ }
                }}
                playLabel={t("analysis.play")}
                pauseLabel={t("analysis.pause")}
              />
            )}
            {optimizedScores && (
              <div className="mt-2 pt-3 border-t border-white/5">
                <div className="tiny-caps mb-2">{t("analysis.score_improvements")}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.keys(optimizedScores).map((k) => {
                    const delta = optimizedScores[k] - scores[k];
                    return (
                      <div key={k} className="flex items-center justify-between">
                        <span className="text-[color:var(--fg-secondary)] capitalize text-xs">
                          {metricLabel(k) || k}
                        </span>
                        <DeltaBadge delta={delta} value={optimizedScores[k]} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Le tue versioni masterizzate · accordion list of all generated
            optimizations (WAV + PDF per version). Shows only after the first
            optimize job completes. */}
        {optimizations.length > 0 && (
          <section
            ref={masteredVersionsRef}
            className="card-elev p-6 mb-10 fade-up"
            style={{ animationDelay: "195ms" }}
            data-testid="mastered-versions-section"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 size={18} className="text-[color:var(--brand)]" />
                  <h3 className="font-display text-xl font-semibold">
                    {t("analysis.versions_title")}
                  </h3>
                </div>
                <p className="text-sm text-[color:var(--fg-secondary)] max-w-2xl">
                  {t("analysis.versions_desc")}
                </p>
              </div>
              <button
                type="button"
                className="btn-primary flex items-center gap-2 self-start md:self-auto"
                onClick={openAdditionalModal}
                data-testid="generate-additional-mastering-btn"
              >
                <Wand2 size={16} /> {t("analysis.generate_additional_btn")}
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <OriginalCompactCard
                analysisId={id}
                lufs={meas?.lufs_integrated}
                t={t}
              />
              {optimizations.map((o, idx) => (
                <VersionCard
                  key={o.opt_id}
                  version={o}
                  index={idx}
                  analysisId={id}
                  t={t}
                />
              ))}
            </div>
          </section>
        )}

        <section className="mb-10 fade-up" style={{ animationDelay: "210ms" }}>
          <FonicoChat analysisId={id} initialHistory={chat_history || []} />
        </section>

        {/* Compare */}
        <section className="card p-6 fade-up mb-16" style={{ animationDelay: "240ms" }}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <GitCompareArrows size={18} className="text-[color:var(--brand)]" />
                <h3 className="font-display text-xl font-semibold">{t("analysis.compare_title")}</h3>
              </div>
              <p className="text-sm text-[color:var(--fg-secondary)]">
                {t("analysis.compare_desc")}
              </p>
            </div>
            <div>
              <input
                ref={compareInput}
                type="file"
                accept=".mp3,.aiff,.aif,.flac,.wav"
                className="hidden"
                onChange={onCompareFile}
                data-testid={ANALYSIS.compareInput}
              />
              <button
                className="btn-ghost flex items-center gap-2"
                onClick={() => compareInput.current?.click()}
                disabled={comparing || (comparisons?.length || 0) >= 1}
                data-testid={ANALYSIS.compareBtn}
              >
                {comparing ? <Loader2 className="animate-spin" size={16} /> : <GitCompareArrows size={16} />}
                {t("analysis.compare_btn")}
              </button>
            </div>
          </div>

          {comparisons?.length > 0 && (
            <div className="flex flex-col gap-4">
              {comparisons.map((c) => (
                <div key={c.compare_id} className="card-elev p-4" data-testid={ANALYSIS.compareResult}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-white font-medium truncate">{c.filename}</div>
                    <div className="metric-value text-xs text-[color:var(--fg-secondary)]">
                      {t("analysis.compare_hit_score", { value: c.analysis.scores.hit_potential })}
                    </div>
                  </div>
                  {c.waveform && (
                    <div className="mb-3">
                      <Waveform
                        data={c.waveform}
                        color="var(--meter-excellent)"
                        secondary={waveform || []}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.keys(c.delta).map((k) => (
                      <div key={k} className="flex items-center justify-between text-sm">
                        <span className="text-[color:var(--fg-secondary)] capitalize text-xs">
                          {metricLabel(k) || k.replace(/_/g, " ")}
                        </span>
                        <DeltaBadge delta={c.delta[k]} value={c.analysis.scores[k]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      {additionalModalOpen && (
        <AdditionalMasteringModal
          t={t}
          presets={lufsPresets}
          usedPresets={optimizations.map((o) => o.lufs_preset)}
          selectedPreset={additionalPreset}
          onPresetChange={onAdditionalPresetChange}
          hasReference={Boolean(doc?.reference)}
          useReference={additionalUseReference}
          onToggleReference={onAdditionalToggleReference}
          precheck={additionalPrecheck}
          submitting={additionalSubmitting}
          onConfirm={confirmAdditional}
          onClose={() => setAdditionalModalOpen(false)}
        />
      )}
    </div>
  );

}

const SmallStat = ({ label, value }) => (
  <div className="card p-3">
    <div className="tiny-caps text-[9px]">{label}</div>
    <div className="metric-value text-white text-sm mt-1">{value}</div>
  </div>
);

const SegBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
      active
        ? "bg-[color:var(--brand)] border-[color:var(--brand)] text-white"
        : "border-white/10 text-[color:var(--fg-secondary)] hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

const DeltaBadge = ({ delta, value }) => {
  const positive = delta > 0.5;
  const negative = delta < -0.5;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = positive
    ? "text-[color:var(--meter-excellent)]"
    : negative
      ? "text-[color:var(--meter-poor)]"
      : "text-[color:var(--fg-muted)]";
  return (
    <div className="flex items-center gap-1.5">
      <span className="metric-value text-white">{Math.round(value)}</span>
      <span className={`flex items-center gap-0.5 text-xs metric-value ${color}`}>
        <Icon size={12} />
        {delta > 0 ? "+" : ""}
        {delta.toFixed(1)}
      </span>
    </div>
  );
};

function PlayerRow({ label, src, testid, highlight, audioRef: externalRef, onProgress, onPlayStart, playLabel = "Play", pauseLabel = "Pause" }) {
  const localRef = useRef(null);
  const audioRef = externalRef || localRef;
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      return;
    }
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        setPlaying(false);
        // Only surface a toast when the source is genuinely broken.
        // NotAllowedError (autoplay policy / user gesture race) and
        // AbortError (rapid play/pause) are silent · they don't mean the
        // audio is missing.
        if (err && (err.name === "NotSupportedError" || err.name === "NotFoundError")) {
          toast.error(t("analysis.toast_audio_unavailable", { defaultValue: "Audio non disponibile · riprova o rigenera la traccia." }));
        }
      });
    }
  };
  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration || !isFinite(a.duration) || !onProgress) return;
    onProgress(a.currentTime / a.duration);
  };
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        highlight ? "border-[color:var(--brand)]/40 bg-blue-500/5" : "border-white/5 bg-white/[0.02]"
      }`}
    >
      <button
        onClick={toggle}
        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center shrink-0"
        aria-label={playing ? pauseLabel : playLabel}
      >
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="tiny-caps mb-1">{label}</div>
        <audio
          ref={audioRef}
          src={src}
          onPlay={() => {
            setPlaying(true);
            // Notify the parent so it can pause the sibling player · makes
            // Original vs Optimized mutually exclusive without extra UI.
            onPlayStart?.();
          }}
          onPause={() => setPlaying(false)}
          onError={(e) => {
            setPlaying(false);
            // Only toast on MEDIA_ERR_SRC_NOT_SUPPORTED (4) · the
            // "real" broken-source case. Codes 1 (aborted) / 2 (network
            // blip) / 3 (decode) are usually transient or user-initiated.
            const code = e?.target?.error?.code;
            if (code === 4) {
              toast.error(t("analysis.toast_audio_unavailable", { defaultValue: "Audio non disponibile · riprova o rigenera la traccia." }));
            }
          }}
          onEnded={() => {
            setPlaying(false);
            onProgress?.(0);
          }}
          onTimeUpdate={handleTimeUpdate}
          controls
          className="w-full"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

/**
 * Card for a single mastered version inside the "Le tue versioni
 * masterizzate" accordion. Shows the LUFS target, creation time, an audio
 * preview, and the WAV/PDF download buttons.
 */
function OriginalCompactCard({ analysisId, lufs, t }) {
  return (
    <div
      className="rounded-lg border border-white/5 bg-white/[0.01] px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="version-card-original"
    >
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <span className="text-[10px] uppercase tracking-wider text-[color:var(--fg-muted)]">
          {t("analysis.versions_original_label", { defaultValue: "Originale" })}
        </span>
        {typeof lufs === "number" && (
          <span className="metric-value text-[10px] text-[color:var(--fg-muted)] tabular-nums">
            {lufs} LUFS
          </span>
        )}
      </div>
      <audio
        controls
        src={audioUrl(analysisId, "original")}
        className="flex-1 min-w-[240px] h-9"
        data-testid="mastered-audio-original"
      />
    </div>
  );
}

function VersionCard({ version, index, analysisId, t }) {
  const isFirst = index === 0;
  const summary = version.summary || {};
  const lufsTarget = summary.loudness_target_lufs;
  const presetLabel = translatePresetLabel(
    version.lufs_preset,
    summary.lufs_preset_label || version.lufs_preset || "custom",
    t,
  );
  const wavHref = downloadUrl(analysisId, version.opt_id);
  const pdfHref = pdfUrl(analysisId, version.opt_id);
  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-3"
      data-testid={`version-card-${version.opt_id}`}
      data-preset={version.lufs_preset}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="tiny-caps text-[color:var(--brand)] shrink-0">#{index + 1}</span>
          <span className="text-white font-medium truncate">
            {presetLabel}
          </span>
          {typeof lufsTarget === "number" && (
            <span className="metric-value text-xs text-[color:var(--fg-secondary)] tabular-nums shrink-0">
              {lufsTarget} LUFS
            </span>
          )}
          <span
            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 font-semibold ${
              isFirst
                ? "bg-[color:var(--meter-excellent)]/20 text-[color:var(--meter-excellent)]"
                : "bg-[color:var(--brand)]/20 text-[color:var(--brand)]"
            }`}
          >
            {isFirst ? t("analysis.versions_included_badge") : t("analysis.versions_extra_badge")}
          </span>
        </div>
        <div className="text-[10px] text-[color:var(--fg-muted)] tabular-nums shrink-0">
          {version.created_at
            ? new Date(version.created_at).toLocaleString()
            : ""}
        </div>
      </div>
      <audio
        controls
        src={audioOptimizationUrl(analysisId, version.opt_id)}
        className="w-full"
        onError={(e) => {
          if (e?.target?.error?.code === 4) {
            toast.error(t("analysis.version_audio_unavailable", { defaultValue: "Audio non disponibile" }));
          }
        }}
        data-testid={`mastered-audio-${version.lufs_preset}`}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={wavHref}
          className="btn-primary text-sm flex items-center gap-2 !py-2 !px-3 no-underline"
          data-testid={`download-wav-${version.lufs_preset}`}
          onClick={() => playDownloadStart()}
        >
          <Download size={14} /> {t("analysis.version_download_wav")}
        </a>
        <a
          href={pdfHref}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-sm flex items-center gap-2 !py-2 !px-3"
          data-testid={`download-pdf-${version.lufs_preset}`}
          onClick={() => playDownloadStart()}
        >
          <FileText size={14} /> {t("analysis.version_download_pdf")}
        </a>
      </div>
    </div>
  );
}

/**
 * Confirm modal for the "genera versione aggiuntiva" flow. Explains cost
 * (+1 credit), lets the user pick a LUFS preset (blocking those already
 * generated), and confirms before spending the credit.
 */
function AdditionalMasteringModal({
  t,
  presets,
  usedPresets,
  selectedPreset,
  onPresetChange,
  hasReference,
  useReference,
  onToggleReference,
  precheck,
  submitting,
  onConfirm,
  onClose,
}) {
  const used = new Set(usedPresets || []);
  const referenceUsed = used.has("reference");
  // A selection is considered "used" only if the corresponding target
  // (reference or a LUFS preset) has already been mastered.
  const selectedUsed = useReference ? referenceUsed : used.has(selectedPreset);
  const noCredits =
    precheck &&
    precheck.credits_available !== null &&
    precheck.credits_available !== undefined &&
    precheck.credits_available < (precheck.credits_needed || 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
      data-testid="additional-mastering-modal"
    >
      <div className="card-elev max-w-md w-full p-6 flex flex-col gap-4">
        <div>
          <h3 className="font-display text-xl font-semibold mb-1">
            {t("analysis.additional_modal_title")}
          </h3>
          <p className="text-sm text-[color:var(--fg-secondary)]">
            {t("analysis.additional_modal_desc")}
          </p>
        </div>
        <div>
          <div className="tiny-caps mb-2">
            {t("analysis.additional_modal_lufs_label")}
          </div>
          {hasReference && (
            <label
              className={`flex items-start gap-2 text-xs mb-3 px-3 py-2 rounded-md border ${
                referenceUsed
                  ? "border-white/10 bg-white/[0.02] opacity-50 cursor-not-allowed"
                  : useReference
                    ? "border-[color:var(--brand)]/40 bg-blue-500/5 cursor-pointer"
                    : "border-white/10 bg-white/[0.02] cursor-pointer hover:border-white/20"
              } select-none`}
              data-testid="additional-use-reference-label"
              title={referenceUsed ? t("analysis.preset_already_used") : undefined}
            >
              <input
                type="checkbox"
                checked={useReference}
                disabled={referenceUsed || submitting}
                onChange={(e) => onToggleReference?.(e.target.checked)}
                className="accent-[color:var(--brand)] mt-0.5 shrink-0"
                data-testid="additional-use-reference-toggle"
              />
              <span className="text-[color:var(--fg-secondary)] leading-snug">
                {t("analysis.reference_toggle", {
                  defaultValue:
                    "Ottimizza verso reference (usa i suoi parametri come target invece dei preset)",
                })}
              </span>
              {referenceUsed && (
                <span className="text-[9px] tiny-caps text-emerald-300/80 ml-auto shrink-0">✓</span>
              )}
            </label>
          )}
          <div className="flex flex-wrap gap-2" data-testid="additional-lufs-selector">
            {presets.map((p) => {
              const isUsed = used.has(p.key);
              const isSelected = !useReference && selectedPreset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  disabled={isUsed || submitting}
                  onClick={() => onPresetChange(p.key)}
                  className={`pill ${isSelected ? "active" : ""} ${
                    isUsed ? "opacity-40 cursor-not-allowed line-through" : ""
                  }`}
                  data-testid={`additional-lufs-${p.key}`}
                  title={
                    isUsed
                      ? t("analysis.additional_modal_lufs_already_generated")
                      : p.lufs != null
                        ? `${p.lufs} LUFS`
                        : ""
                  }
                >
                  <span className="font-medium">{p.label.split(" (")[0]}</span>
                  {p.lufs != null && (
                    <span className="metric-value text-[10px] opacity-70 ml-1.5">
                      {p.lufs} LUFS
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-lg border border-[color:var(--brand)]/30 bg-blue-500/5 p-3 flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[color:var(--fg-secondary)]">
              {t("analysis.additional_modal_cost")}
            </span>
            <span className="text-white font-semibold">−1</span>
          </div>
          {precheck && precheck.credits_available !== null && precheck.credits_available !== undefined && (
            <>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[color:var(--fg-muted)]">
                  {t("analysis.additional_modal_credits_remaining", {
                    n: precheck.credits_available,
                  })}
                </span>
                <span className="text-white metric-value">
                  {precheck.credits_available}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[color:var(--fg-muted)]">
                  {t("analysis.additional_modal_credits_after", {
                    n: Math.max(0, precheck.credits_available - (precheck.credits_needed || 0)),
                  })}
                </span>
                <span className="text-white metric-value">
                  {Math.max(0, precheck.credits_available - (precheck.credits_needed || 0))}
                </span>
              </div>
            </>
          )}
        </div>
        {noCredits && (
          <div
            className="text-xs text-[color:var(--meter-poor)] bg-red-500/10 border border-red-500/30 rounded-md p-2"
            data-testid="additional-no-credits"
          >
            {t("analysis.additional_modal_no_credits")}
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
            disabled={submitting}
            data-testid="additional-cancel-btn"
          >
            {t("analysis.additional_modal_cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm flex items-center gap-2"
            onClick={onConfirm}
            disabled={submitting || selectedUsed || noCredits}
            data-testid="additional-confirm-btn"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : null}
            {t("analysis.additional_modal_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
